import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertMessage from "../common/AlertMessage";
import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import { useAppContext } from "../AppContext";
import ComposeTab from "./ComposeTab";
import AutomaticMailsTab, { getDueMails } from "./AutomaticMailsTab";
import HistoryTab from "./HistoryTab";
import PlannedTab from "./PlannedTab";
import UnsubscribesTab from "./UnsubscribesTab";

const TABS = [
  { key: 'compose', icon: 'icon-mail' },
  { key: 'automatic', icon: 'icon-clock' },
  { key: 'planned', icon: 'icon-calendar' },
  { key: 'history', icon: 'icon-history' },
  { key: 'unsubscribes', icon: 'icon-cross-circle' },
];

const MailingPage = () => {
  const { t } = useTranslation();
  const { alertMessage } = useAppContext();
  const [activeTab, setActiveTab] = useState('compose');
  const [historyKey, setHistoryKey] = useState(0);
  const alertRef = useRef(null);

  // Save/send buttons sit at the bottom of long tabs, so bring the result message into view
  useEffect(() => {
    if (alertMessage.content) alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [alertMessage]);

  const { data: audience, loading: audienceLoading, refetch: refetchAudience } = useAxiosFetch('/api/mailings/audience');
  const { data: settings, loading: settingsLoading } = useAxiosFetch('/api/mailings/settings');

  if (audienceLoading || settingsLoading) return <LoadPage />;
  if (!audience || !settings) {
    return (
      <div id="content">
        <AlertMessage type={alertMessage.type} message={alertMessage.content} />
        <div className="content-bubble">{t('mailing.load_error')}</div>
      </div>
    );
  }

  const afterSend = () => {
    refetchAudience();
    setHistoryKey((key) => key + 1);
    setActiveTab('history');
  };

  const dueCount = getDueMails(audience).length;
  const stats = [
    { key: 'expired', icon: 'icon-history', tone: 'red', value: audience.filter((env) => env.status === 'trial_expired' || env.status === 'subscription_expired').length },
    { key: 'trials', icon: 'icon-clock', tone: 'blue', value: audience.filter((env) => env.status === 'trial').length },
    { key: 'due', icon: 'icon-mail', tone: 'orange', value: dueCount },
    { key: 'subscriptions', icon: 'icon-check-circle', tone: 'green', value: audience.filter((env) => env.status === 'subscription').length },
  ];

  return (
    <div id="content" className="mail-page">
      <div id="content-title">{t('mailing.title')}</div>
      <div className="clear-both" />
      <p className="mail-lead">{t('mailing.intro')}</p>

      <div className="mail-stats">
        {stats.map((stat) => (
          <div key={stat.key} className="mail-stat">
            <span className={`mail-stat-icon mail-tone-${stat.tone}`}><i className={stat.icon} /></span>
            <div>
              <div className="mail-stat-value">{stat.value}</div>
              <div className="mail-stat-label">{t(`mailing.stat_${stat.key}`)}</div>
            </div>
          </div>
        ))}
      </div>

      <div ref={alertRef}>
        <AlertMessage type={alertMessage.type} message={alertMessage.content} />
      </div>

      <div className="tab-container">
        {TABS.map((tab) => (
          <div key={tab.key} onClick={() => setActiveTab(tab.key)} className={`tab-item ${activeTab === tab.key ? 'active' : ''}`}>
            <i className={tab.icon} /> {t(`mailing.tab_${tab.key}`)}
            {tab.key === 'automatic' && dueCount > 0 && <span className="mail-tab-badge">{dueCount}</span>}
          </div>
        ))}
      </div>

      <div className="content-bubble mail-body">
        {/* Compose and automatic mails stay mounted so unsaved texts survive switching tabs */}
        <div hidden={activeTab !== 'compose'}>
          <ComposeTab active={activeTab === 'compose'} audience={audience} defaults={settings.defaults} drafts={settings.campaigns} onSent={afterSend} />
        </div>
        <div hidden={activeTab !== 'automatic'}>
          <AutomaticMailsTab
            active={activeTab === 'automatic'}
            settings={settings}
            defaults={settings.defaults}
            audience={audience}
            onSent={afterSend}
          />
        </div>
        {activeTab === 'planned' && <PlannedTab />}
        {activeTab === 'history' && <HistoryTab refreshKey={historyKey} />}
        {activeTab === 'unsubscribes' && <UnsubscribesTab />}
      </div>
    </div>
  );
};

export default MailingPage;
